/**
 * KioskGeraetDetail — Detailsicht eines Stempel-Terminals (#1814).
 *
 * Zeigt je Geraet, wer wann daran gestempelt hat, wann und von wem es
 * gekoppelt wurde und wann es zuletzt aktiv war. Der Widerruf bleibt hier
 * erreichbar, damit ein auffaelliges Geraet ohne Umweg entzogen werden kann.
 *
 * Das Protokoll ist eine Sicht auf `stempelzeiten.kiosk_geraet_id` — dieselbe
 * Struktur, aus der auch Zeitkonto und Monatsnachweis lesen. Buchungen ohne
 * Geraet (Handy, Portal) tauchen hier bewusst nicht auf.
 *
 * @component
 * @endpoints GET /api/kiosk-geraete/:id, POST /api/kiosk-geraete/:id/widerrufen
 * @since v2.869.0
 */
import { useState, useEffect, useCallback } from 'react';
import { X, MonitorSmartphone, Clock } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const TYP_KEY = {
  arbeit_start: 'attendance.typ.arbeit_start',
  arbeitsende: 'attendance.typ.arbeitsende',
  kurze_pause: 'attendance.typ.kurze_pause',
  pause_ende: 'attendance.typ.pause_ende',
  mittagspause: 'attendance.typ.mittagspause',
  mittag_ende: 'attendance.typ.mittag_ende',
};

const TYP_FALLBACK = {
  arbeit_start: 'Beginn',
  arbeitsende: 'Ende',
  kurze_pause: 'Kurze Pause',
  pause_ende: 'Pause Ende',
  mittagspause: 'Mittag',
  mittag_ende: 'Mittag Ende',
};

function datum(wert) {
  if (!wert) return '–';
  return new Date(wert).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

export default function KioskGeraetDetail({ geraetId, t, showToast, onClose, onChanged }) {
  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(true);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/kiosk-geraete/${geraetId}`, { credentials: 'include' });
      if (!res.ok) { showToast('Detail konnte nicht geladen werden'); return; }
      setDaten(await res.json());
    } finally {
      setLoading(false);
    }
  }, [geraetId, showToast]);

  useEffect(() => { laden(); }, [laden]);

  const widerrufen = async () => {
    const res = await fetch(`${API}/api/kiosk-geraete/${geraetId}/widerrufen`, {
      method: 'POST', credentials: 'include',
    });
    if (!res.ok) { showToast('Widerruf fehlgeschlagen'); return; }
    showToast('Registrierung widerrufen');
    await laden();
    onChanged?.();
  };

  const g = daten?.geraet;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <div
        className="bg-surface-elevated rounded-card border border-line w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <MonitorSmartphone className="w-4 h-4 flex-shrink-0" style={{ color: '#338edc' }} />
            <h2 className="text-sm font-semibold text-content truncate">
              {g?.name || t('stempel.terminals.detail', 'Terminal')}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg" aria-label={t('common.close', 'Schließen')}>
            <X className="w-4 h-4" style={{ color: 'var(--text-tertiary, #6B7280)' }} />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {loading ? (
            <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>...</p>
          ) : !g ? (
            <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('stempel.terminals.nichtGefunden', 'Terminal nicht gefunden.')}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <Kennzahl label={t('stempel.terminals.standort', 'Standort')} wert={g.standort || '–'} />
                <Kennzahl label={t('stempel.terminals.gekoppeltAm', 'Gekoppelt')} wert={datum(g.gekoppelt_am)} />
                <Kennzahl label={t('stempel.terminals.letzteAktivitaet', 'Letzte Aktivität')} wert={datum(g.letzte_nutzung)} />
                <Kennzahl label={t('stempel.terminals.stempelGesamt', 'Stempel gesamt')} wert={String(daten.stempel_gesamt)} />
              </div>

              <p className="text-xs mb-4" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('stempel.terminals.angelegtVon', 'Angelegt')} {datum(g.erstellt_am)}
                {g.erstellt_von_name ? ` von ${g.erstellt_von_name}` : ''}
                {g.widerrufen_am
                  ? ` · ${t('stempel.terminals.widerrufenAm', 'widerrufen')} ${datum(g.widerrufen_am)}${g.widerrufen_von_name ? ` von ${g.widerrufen_von_name}` : ''}`
                  : ''}
              </p>

              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-3.5 h-3.5" style={{ color: 'var(--text-tertiary, #6B7280)' }} />
                <h3 className="text-xs font-semibold text-content">
                  {t('stempel.terminals.log', 'Stempel an diesem Terminal')}
                </h3>
              </div>

              {daten.log.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                  {t('stempel.terminals.logLeer', 'An diesem Terminal wurde noch nicht gestempelt.')}
                </p>
              ) : (
                <div className="space-y-1">
                  {daten.log.map(e => (
                    <div
                      key={e.id}
                      className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg border border-line"
                    >
                      <span className="text-sm text-content truncate flex items-center gap-1.5">
                        {e.mitarbeiter_name || `#${e.mitarbeiter_id}`}
                        {e.offline_nachgetragen && (
                          <span
                            className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-500 flex-shrink-0"
                            title={t('stempel.terminals.offlineHinweis', 'Offline erfasst und später nachgeholt — die Uhrzeit stammt vom Tablet.')}
                          >
                            {t('stempel.terminals.offlineKurz', 'offline')}
                          </span>
                        )}
                      </span>
                      <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                        {t(TYP_KEY[e.typ] || '', TYP_FALLBACK[e.typ] || e.typ)} · {datum(e.zeitpunkt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {g && g.status !== 'widerrufen' && (
          <div className="px-5 py-3 border-t border-line flex justify-end">
            <button onClick={widerrufen} className="text-xs underline" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('stempel.terminals.widerrufen', 'Widerrufen')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Kennzahl({ label, wert }) {
  return (
    <div>
      <p className="text-[11px] mb-0.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{wert}</p>
    </div>
  );
}
